// The boards as tabs (Features 7, 30 Oct; Features 8, 1 Nov): a tab for each board in the order All boards keeps, so
// one order shows everywhere. Switched on from the board menu and kept with the account, as is its look: floating in one
// island where the pill sits (David's pick, 1 Nov), or a bar across the top, with the boards' icons or without. The open
// tab shows its whole name and a click on it opens the board's menu, as a right-click on any tab does; the others
// shorten a long one, whole on hover. A folder is a coloured dot and its name: a click opens its boards in place beside
// it, tinted together, or folds them to a count, and one holding the board you are on opens by itself. The strip scrolls
// with the board you are on in view, sideways to a mouse wheel, its edges fading while more wait; dragging a tab
// reorders the list through the same moves the boards list makes (shared/boardsOrder.ts). Full screen's bar holds the
// same strip (TabStrip).

import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LayoutGrid, Maximize2, Plus } from "lucide-react";
import type { Board, BoardFolder } from "../../shared/types";
import { topLevel } from "../../shared/boardsOrder";
import { cx } from "../lib/util";
import { dropBoard, placeAboveFolder, placeFolder, setBoardFolder, switchBoard } from "../state/actions";
import { set, useStore } from "../state/store";
import { menuJustShut, openContextMenu } from "./ContextMenu";
import { TabsLook } from "./menus/BoardActions";
import { openOverview } from "./Panels";
import { IconButton } from "./ui";
import { SaveDot } from "./TopBar";
import { KIND_ICON } from "../boards/kinds";
import { kindOf } from "../../shared/boardKinds";
import { enterFullscreen, TAB_KINDS } from "./Fullscreen";
import { readStoredJson, writeStoredJson } from "../lib/stored";
import { useTabLook } from "./useTabs";

const NO_FOLDERS: BoardFolder[] = [];
/** The buttons top right's width the last time they were measured, so a bar drawn again starts where it ended. */
let lastRight = 340;
/** The room kept between the open tab and the strip's edge when it scrolls to show it. */
const EDGE_ROOM = 28;
/** A folder's colour, by its place in the list: the theme's own text colours, which read on the bar. */
const GROUP_COLORS = ["orange", "blue", "green", "purple", "red", "yellow"];
/** Folders opened or folded in the tabs on this computer, by id; one left out opens while it holds the board you are on. */
const FOLDS = "cb:tabs:folds";

/** A board's emoji, or its kind's icon when it has none, in one 16px slot so every tab's mark sits on the same line. */
function Mark({ board }: { board: Board }) {
  const Icon = KIND_ICON[kindOf(board)] ?? KIND_ICON.canvas;
  return <span className="board-tab-ico">{board.emoji ? <span className="board-emoji">{board.emoji}</span> : <Icon size={15} strokeWidth={1.75} className="board-kind-mark" aria-hidden />}</span>;
}

/** What is being dragged, and what it is over: a tab's left edge (before it) or its middle (into a folder, or its place). */
type Drag = { kind: "board" | "folder"; id: string };
type Over = { id: string; edge: boolean } | null;

/** The tabs themselves, in the top bar and in full screen's. */
export function TabStrip({ board }: { board: Board }) {
  const boards = useStore((s) => s.boards);
  const list = useStore((s) => s.workspace?.folders ?? NO_FOLDERS);
  const lines = topLevel(list, boards);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [over, setOver] = useState<Over>(null);
  // the folders opened or folded here (FOLDS)
  const [folds, setFolds] = useState<Record<string, boolean>>(() => {
    const kept = readStoredJson<unknown>(FOLDS, {});
    return kept && typeof kept === "object" && !Array.isArray(kept) ? (kept as Record<string, boolean>) : {};
  });
  // a folder just opened: its boards are brought into view
  const reveal = useRef<string | null>(null);
  const fold = (id: string, open: boolean) =>
    setFolds((was) => {
      reveal.current = open ? id : null;
      // only the folders still there are kept
      const next = Object.fromEntries(Object.entries({ ...was, [id]: open }).filter(([k]) => list.some((f) => f.id === k)));
      writeStoredJson(FOLDS, next);
      return next;
    });
  // a tab's name on hover, drawn outside the strip: the strip scrolls, and would cut a tooltip off
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(null);
  const tipTimer = useRef<number>(0);
  const hint = (text: string) => ({
    onPointerEnter: (e: React.PointerEvent<HTMLElement>) => {
      window.clearTimeout(tipTimer.current);
      if (e.pointerType === "touch") return;
      const r = e.currentTarget.getBoundingClientRect();
      tipTimer.current = window.setTimeout(() => setTip({ text, x: r.left + r.width / 2, y: r.bottom + 9 }), 450);
    },
    onPointerLeave: () => (window.clearTimeout(tipTimer.current), setTip(null)),
    onPointerDown: () => (window.clearTimeout(tipTimer.current), setTip(null)),
  });
  useEffect(() => () => window.clearTimeout(tipTimer.current), []);
  const scroller = useRef<HTMLDivElement>(null);
  const endDrag = () => (setDrag(null), setOver(null));
  // a tab dragged to the strip's edge carries it on, so a board can be placed beyond what shows
  const edgeScroll = (e: React.DragEvent) => {
    const sc = scroller.current;
    if (!sc) return;
    const r = sc.getBoundingClientRect();
    if (e.clientX < r.left + 40) sc.scrollLeft -= 14;
    else if (e.clientX > r.right - 40) sc.scrollLeft += 14;
  };

  // the board you are on always in view, as the strip scrolls; the edges fade, with an arrow, where more tabs wait
  const [edge, setEdge] = useState({ l: false, r: false });
  useLayoutEffect(() => {
    const sc = scroller.current;
    if (!sc) return;
    const fades = () => {
      const l = sc.scrollLeft > 2;
      const r = sc.scrollLeft + sc.clientWidth < sc.scrollWidth - 2;
      setEdge((e) => (e.l === l && e.r === r ? e : { l, r }));
    };
    const show = () => {
      const on = sc.querySelector<HTMLElement>(".board-tab.is-current");
      if (on) {
        const l = on.offsetLeft;
        if (l < sc.scrollLeft + EDGE_ROOM) sc.scrollLeft = l - EDGE_ROOM;
        else if (l + on.offsetWidth > sc.scrollLeft + sc.clientWidth - EDGE_ROOM) sc.scrollLeft = l + on.offsetWidth - sc.clientWidth + EDGE_ROOM;
      }
      fades();
    };
    show();
    // a mouse wheel moves the strip sideways, as in a browser's tab bar
    const wheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) || sc.scrollWidth <= sc.clientWidth) return;
      e.preventDefault();
      sc.scrollLeft += e.deltaY;
    };
    const ro = new ResizeObserver(show);
    ro.observe(sc);
    sc.addEventListener("scroll", fades, { passive: true });
    sc.addEventListener("wheel", wheel, { passive: false });
    return () => {
      ro.disconnect();
      sc.removeEventListener("scroll", fades);
      sc.removeEventListener("wheel", wheel);
    };
  }, [board.id, lines.length, boards.length, folds]);
  useLayoutEffect(() => {
    const sc = scroller.current;
    const id = reveal.current;
    reveal.current = null;
    const last = id && sc ? [...sc.querySelectorAll<HTMLElement>(`[data-group="${CSS.escape(id)}"]`)].pop() : null;
    if (!sc || !last) return;
    const right = last.offsetLeft + last.offsetWidth + EDGE_ROOM;
    if (right > sc.scrollLeft + sc.clientWidth) sc.scrollTo({ left: right - sc.clientWidth, behavior: "smooth" });
  }, [folds]);
  // the arrow keys move between tabs, as in a browser's tab bar
  const arrows = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const all = [...(scroller.current?.querySelectorAll<HTMLElement>(".board-tab-main, .board-tab-label") ?? [])];
    const at = all.indexOf(document.activeElement as HTMLElement);
    if (at < 0) return;
    e.preventDefault();
    all[Math.max(0, Math.min(all.length - 1, at + (e.key === "ArrowRight" ? 1 : -1)))]?.focus();
  };

  const boardMenu = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    openContextMenu({
      x: e.clientX,
      y: e.clientY,
      kind: "board",
      ids: [id],
      at: null,
    });
  };
  // a click on another tab opens its board; on the open one, the board's menu, under it (David, 1 Nov: no ⋯ on the tab)
  const pick = (id: string, el: HTMLElement) => {
    if (id !== board.id) return switchBoard(id);
    if (menuJustShut(id, el)) return;
    const r = el.getBoundingClientRect();
    openContextMenu({ x: r.left, y: r.bottom + 8, kind: "board", ids: [id], at: null });
  };
  // a drag that ends where its tab no longer is (a board taken out of a folded folder) is still ended
  useEffect(() => {
    if (!drag) return;
    window.addEventListener("dragend", endDrag);
    window.addEventListener("drop", endDrag);
    return () => {
      window.removeEventListener("dragend", endDrag);
      window.removeEventListener("drop", endDrag);
    };
  }, [drag]); // eslint-disable-line react-hooks/exhaustive-deps
  const dragProps = (d: Drag, key: string) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", key);
      setDrag(d);
    },
    onDragEnd: endDrag,
  });
  /** The left quarter of a tab is the edge: a board dropped there stands before a folder rather than inside it. */
  const edgeOf = (e: React.DragEvent) => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientX < r.left + r.width / 4;
  };
  const overBoard = (e: React.DragEvent, id: string) => {
    if (!drag || drag.id === id) return;
    e.preventDefault();
    if (over?.id !== id || over.edge) setOver({ id, edge: false });
  };
  const overFolder = (e: React.DragEvent, id: string) => {
    if (!drag || drag.id === id) return;
    e.preventDefault();
    const edge = drag.kind === "folder" || edgeOf(e);
    if (over?.id !== id || over.edge !== edge) setOver({ id, edge });
  };

  const tab = (b: Board, here: boolean, group?: { id: string; color: string }) => (
    <div
      key={b.id}
      className={cx("board-tab", here && "is-current", group && "in-group", drag?.id === b.id && "is-dragging", over?.id === b.id && "is-drop")}
      style={group ? ({ "--grp": group.color } as React.CSSProperties) : undefined}
      data-group={group?.id}
      {...(b.archived ? {} : dragProps({ kind: "board", id: b.id }, b.name))}
      onDragOver={(e) => overBoard(e, b.id)}
      onDragLeave={() => over?.id === b.id && setOver(null)}
      onDrop={(e) => {
        e.preventDefault();
        if (drag?.kind === "board") dropBoard(drag.id, b.id);
        else if (drag) placeFolder(drag.id, { board: b.id });
        endDrag();
      }}
      onContextMenu={(e) => boardMenu(e, b.id)}
    >
      <button className="board-tab-main" aria-current={here ? "page" : undefined} aria-label={b.name} aria-haspopup={here ? "menu" : undefined} {...hint(here ? "Board menu" : b.name)} onClick={(e) => pick(b.id, e.currentTarget)}>
        <Mark board={b} />
        <span className="board-tab-name">{b.name}</span>
      </button>
    </div>
  );

  // a folder: its label, in its colour, then its boards beside it while it is open; folded, its count, and the board you
  // are on still beside it, so the strip always says where you are
  const group = (fo: BoardFolder, inside: Board[]) => {
    const holds = inside.some((b) => b.id === board.id);
    const open = folds[fo.id] ?? holds;
    const color = `var(--tc-${GROUP_COLORS[Math.max(0, list.findIndex((f) => f.id === fo.id)) % GROUP_COLORS.length]})`;
    const count = inside.length === 1 ? "1 board" : `${inside.length} boards`;
    return (
      <Fragment key={fo.id}>
        <div
          className={cx("board-tab-group", open && "is-open", holds && "is-holds", drag?.id === fo.id && "is-dragging", over?.id === fo.id && (over.edge ? "is-drop" : "is-drop-in"))}
          style={{ "--grp": color } as React.CSSProperties}
          {...dragProps({ kind: "folder", id: fo.id }, fo.name)}
          onDragOver={(e) => overFolder(e, fo.id)}
          onDragLeave={() => over?.id === fo.id && setOver(null)}
          onDrop={(e) => {
            e.preventDefault();
            if (drag?.kind === "folder") placeFolder(drag.id, { folder: fo.id });
            else if (drag && over?.edge) placeAboveFolder(drag.id, fo.id);
            else if (drag) setBoardFolder(drag.id, fo.id);
            endDrag();
          }}
        >
          <button className="board-tab-label" aria-expanded={open} aria-label={`${fo.name}, a folder of ${count}${open ? "" : ", folded"}`} {...hint(open ? `Fold ${fo.name}` : `${fo.name}: ${count}`)} onClick={() => fold(fo.id, !open)}>
            <i className="board-tab-dot" aria-hidden />
            <span>{fo.name}</span>
            {!open && <span className="board-tab-count">{inside.length}</span>}
          </button>
        </div>
        {(open ? inside : inside.filter((b) => b.id === board.id)).map((b) => tab(b, b.id === board.id, { id: fo.id, color }))}
        {open && !inside.length && (
          <span
            className="board-tab-none"
            onDragOver={(e) => drag?.kind === "board" && e.preventDefault()}
            onDrop={(e) => {
              if (drag?.kind !== "board") return;
              e.preventDefault();
              setBoardFolder(drag.id, fo.id);
              endDrag();
            }}
          >
            Drag a board here
          </span>
        )}
      </Fragment>
    );
  };

  const root = scroller.current?.closest(".board-root") ?? document.body;
  // an archived board in view (read only) has no tab in the strip: it stands at its end, so the strip says where you are
  const shelved = !boards.some((b) => b.id === board.id);

  return (
    <div className={cx("board-tabs-strip", edge.l && "fade-l", edge.r && "fade-r")}>
      <div className="board-tabs-scroll" ref={scroller} onKeyDown={arrows} onDragOver={(e) => drag && (e.preventDefault(), edgeScroll(e))}>
        {lines.map((l) => ("folder" in l ? group(l.folder, l.boards) : tab(l.board, l.board.id === board.id)))}
        {shelved && tab(board, true)}
      </div>
      {tip &&
        createPortal(
          <div className="board-tab-tip" style={{ left: tip.x, top: tip.y }} role="tooltip">
            {tip.text}
          </div>,
          root,
        )}
    </div>
  );
}

/**
 * The tabs: a bar across the top, Home's mark at its left, the tabs, then All boards, New board and Full screen, the
 * buttons top right at its own (both are drawn by App, into the room this leaves); or the same floating over the board,
 * between the Home mark and the buttons top right (Features 8).
 */
export function BoardTabs({ board }: { board: Board }) {
  const [right, setRight] = useState(lastRight);
  const look = useTabLook();
  // the strip stops where the buttons top right begin, whatever they are just now (the mic, a longer chat label)
  useLayoutEffect(() => {
    const el = document.querySelector<HTMLElement>(".topright");
    if (!el) return;
    const measure = () => setRight((lastRight = Math.round(window.innerWidth - el.getBoundingClientRect().left + 8)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  const tabs = (
    <nav className={cx("board-tabs", !look.bar && "island", !look.icons && "no-icons")} aria-label="Boards">
      <TabStrip board={board} />
      <TabEnd board={board} />
    </nav>
  );
  return look.bar ? (
    <div className="topbar tabbar" style={{ paddingRight: right }}>
      {tabs}
    </div>
  ) : (
    // centred, as the pill is (David, 1 Nov): the same room each side, as much as the buttons top right take
    <div className="topbar tabfloat" style={{ left: Math.max(64, right), right: Math.max(64, right) }}>
      {tabs}
    </div>
  );
}

/**
 * All boards, a new board and the save mark: the strip's end, in the top bar and in full screen's. With board, the top
 * bar's: how the boards show, with the way back to the pill, and Full screen where the pill has it (a doc, sheet, deck
 * or files board fills the tab; David, 1 Nov).
 */
export function TabEnd({ board }: { board?: Board }) {
  return (
    <div className="board-tabs-end">
      <IconButton label="All boards" kbd="G" onClick={() => openOverview()}>
        <LayoutGrid size={15} />
      </IconButton>
      <IconButton label="New board" onClick={() => set({ panel: "newboard", panelArg: null })}>
        <Plus size={15} />
      </IconButton>
      {/* how the boards show, always in view: tabs or the pill, and icons (David, 1 Nov) */}
      {board && <TabsLook />}
      {board && TAB_KINDS.has(kindOf(board)) && (
        <IconButton label="Full screen" kbd="F" onClick={() => void enterFullscreen()}>
          <Maximize2 size={15} />
        </IconButton>
      )}
      <SaveDot />
    </div>
  );
}
